<template>
  <modal
    name="bulk-assign-vehicle-modal"
    :height="isMobile ? '100%' : 'auto'"
    :width="isMobile ? '100%' : '600px'"
    @closed="closeModalHandler"
    class="rounded-lg overflow-hidden"
  >
    <!-- Header -->
    <div class="bg-gradient-to-r from-red-600 to-red-700 py-4 px-5 flex justify-between items-center">
      <h4 class="text-lg font-semibold text-white flex items-center">
        <i class="fas fa-truck-moving mr-2"></i>
        TOPLU ARAÇ GÖREV ATAMA
      </h4>
      <button class="text-white hover:bg-red-800 p-2 rounded-full transition-colors duration-200" @click="closeModal">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Content -->
    <div class="p-5 bg-white dark:bg-gray-800 text-black dark:text-gray-200 max-h-[calc(100vh-150px)] overflow-y-auto">
      <!-- Seçili Araçlar -->
      <div class="mb-6">
        <h5 class="font-bold mb-3 text-gray-700 dark:text-gray-300 flex items-center">
          <i class="fas fa-check-circle mr-2 text-green-600"></i>
          Seçili Araçlar <span class="ml-2 bg-green-600 text-white rounded-full px-2 py-0.5 text-xs">{{ vehicles.length }}</span>
        </h5>
        <div class="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600">
          <div v-if="vehicles.length === 0" class="text-center py-4 text-gray-500 dark:text-gray-400">
            <i class="fas fa-info-circle mr-2"></i>
            Seçili araç bulunmamaktadır
          </div>
          <div 
            v-else
            v-for="vehicle in vehicles" 
            :key="vehicle.id" 
            class="flex justify-between items-center mb-2 p-2 border-b border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors duration-200"
          >
            <div class="flex items-center">
              <span class="bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded font-medium mr-2">{{ vehicle.plaque }}</span>
              <span class="text-sm text-gray-600 dark:text-gray-400">{{ vehicle.NAME }}</span>
            </div>
            <span class="text-xs text-gray-500 dark:text-gray-400">{{ vehicle.dorse || "Dorse Yok" }}</span>
          </div>
        </div>
      </div>
      
      <!-- Sipariş Seçimi -->
      <div class="mb-6">
        <h5 class="font-bold mb-3 text-gray-700 dark:text-gray-300 flex items-center">
          <i class="fas fa-clipboard-list mr-2 text-red-600"></i>
          Atanacak Sipariş
        </h5>
        <div class="relative">
          <select 
            v-model="selectedOrderId" 
            class="w-full p-3 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all duration-200 appearance-none"
          >
            <option value="" disabled>Sipariş seçiniz</option>
            <option
              v-for="order in getAvailableOrders"
              :key="order.id"
              :value="order.id"
            >
              {{ order.orderNo }} - {{ order.orderName }}
            </option>
          </select>
          <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
            <i class="fas fa-chevron-down"></i>
          </div>
        </div>

        <!-- Sipariş Detayları -->
        <transition name="fade">
          <div v-if="selectedOrder" class="mt-4 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg border-l-4 border-blue-600">
            <h6 class="font-semibold mb-2 text-blue-600 dark:text-blue-400">Sipariş Detayı</h6>
            <div class="grid grid-cols-2 gap-3">
              <div class="col-span-2 sm:col-span-1">
                <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Ürün:</span>
                <p>{{ selectedOrder.productType || "Belirtilmemiş" }}</p>
              </div>
              <div class="col-span-2 sm:col-span-1">
                <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Dolum:</span>
                <p>{{ selectedOrder.receivedName || "Belirtilmemiş" }}</p>
              </div>
              <div class="col-span-2 sm:col-span-1">
                <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Teslim:</span>
                <p>{{ selectedOrder.deliveredName || "Belirtilmemiş" }}</p>
              </div>
              <div class="col-span-2 sm:col-span-1">
                <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Sipariş No:</span>
                <p>{{ selectedOrder.orderNo || "Belirtilmemiş" }}</p>
              </div>
            </div>
          </div>
        </transition>
      </div>
      
      <!-- Özet ve Uyarı -->
      <div v-if="selectedOrderId && vehicles.length > 0" class="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
        <div class="flex items-start">
          <i class="fas fa-exclamation-triangle text-yellow-600 dark:text-yellow-500 mt-1 mr-3"></i>
          <div>
            <h6 class="font-semibold text-yellow-800 dark:text-yellow-500">Atama Özeti</h6>
            <p class="text-sm text-yellow-700 dark:text-yellow-400">
              <span class="font-bold">{{ vehicles.length }}</span> araç 
              <span class="font-bold">#{{ selectedOrder?.orderNo }}</span> 
              numaralı siparişe atanacak.
            </p>
          </div>
        </div>
      </div>
      
      <!-- Atama Butonu -->
      <div class="mt-6">
        <asyncBtn
          icon="fas fa-share"
          text="Toplu Göreve Ata"
          :loadState="loading"
          class="w-full hover:shadow-lg transition-all duration-300"
          :disabled="!selectedOrderId || vehicles.length === 0"
          :containerClass="(!selectedOrderId || vehicles.length === 0) ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'"
          @click="assignVehicles"
        />
      </div>
    </div>
  </modal>
</template>

<script>
import asyncBtn from "@/components/general/asyncBtn.vue";
import axios from "axios";
import { bulkShipPlanning } from "@/networking/urlmanager";

export default {
  props: {
    rowId: {
      type: [String, Number],
      required: true
    },
    selectedVehicleIds: {
      type: Array,
      default: () => []
    },
    vehicles: {
      type: Array,
      default: () => []
    },
    orders: {
      type: Array,
      default: () => []
    }
  },
  components: {
    asyncBtn
  },
  data() {
    return {
      selectedOrderId: "",
      loading: false
    };
  },
  computed: {
    getAvailableOrders() {
      return this.orders.filter(r => {
        // Tonaj 0 veya negatif olan siparişleri filtrele
        if (r.tonnage <= 0) return false;

        // Tamamlanmış siparişleri filtrele (isCopmletedOrder = 1)
        if (r.isCopmletedOrder === 1) return false;

        // Kalan tonajı olmayan siparişleri filtrele (tonnage - receivedTonnage = 0)
        const remainingTonnage = Number(r.tonnage) - Number(r.receivedTonnage || 0);
        if (remainingTonnage <= 0) return false;

        return true;
      });
    },
    selectedOrder() {
      return this.orders.find(order => order.id === this.selectedOrderId);
    },
    isMobile() {
      return window.innerWidth < 768;
    }
  },
  methods: {
    closeModal() {
      this.$modal.hide("bulk-assign-vehicle-modal");
    },
    closeModalHandler() {
      this.$emit('clearSelection');
      this.$emit('refresh');
    },
    assignVehicles() {
      if (!this.selectedOrderId || this.vehicles.length === 0) {
        this.swalBox(
          "warning",
          "Uyarı!",
          "Lütfen sipariş seçiniz ve en az bir araç olduğundan emin olunuz",
          false,
          "Tamam"
        );
        return;
      }
      
      this.loading = true;
      
      // Burada backend'e toplu atama isteği gönderilecek
      axios
        .post(
          bulkShipPlanning.bulkAssignVehicles,
          {
            vehicleIds: this.selectedVehicleIds,
            orderId: this.selectedOrderId,
            shipId: this.rowId
          },
          {
            headers: {
              Authorization: "Bareer " + this.$store.state.userData.token
            }
          }
        )
        .then((response) => {
          const message = response?.data?.data || `${this.vehicles.length} araç başarıyla siparişe atandı.`;
          
          this.swalBox(
            "success",
            this.$t("general.successTitle"),
            message,
            false,
            this.$t("general.OkayTitle")
          );
          this.closeModal();
        })
        .catch(err => {
          this.errorBox(err.response);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
